import { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import { AccelerationPoint } from '../domain/telemetry/processed/Acceleration'
import { FrequencyAdaptiveConfig } from '../utils/FrequencyAdaptiveConfig'
import {
  smoothGpsCoordinates,
  smoothValuesGaussian,
} from '../utils/smoothingUtils'
import { calculateDistance } from '../utils/distanceUtils'
import { KMH_PER_MPS } from '../utils/speedUnits'
import { MS_PER_S } from '../utils/timeUnits'

/**
 * Calculate turn radius from three GPS points using circumradius formula
 *
 * Uses Heron's formula to compute the area of the triangle formed by three points,
 * then calculates the circumradius (radius of circle passing through all three points).
 *
 * @returns Turn radius in meters, or null if points are too close/collinear
 */
function calculateTurnRadiusFromPoints(
  p1: GpsPoint,
  p2: GpsPoint,
  p3: GpsPoint
): number | null {
  const d1 = calculateDistance(
    p1.latitude,
    p1.longitude,
    p2.latitude,
    p2.longitude
  )
  const d2 = calculateDistance(
    p2.latitude,
    p2.longitude,
    p3.latitude,
    p3.longitude
  )
  const d3 = calculateDistance(
    p1.latitude,
    p1.longitude,
    p3.latitude,
    p3.longitude
  )

  // Points must have minimum separation (2m) for accurate geometry
  if (d1 < 2 || d2 < 2 || d3 < 2) return null

  // Calculate semi-perimeter and area using Heron's formula
  const s = (d1 + d2 + d3) / 2
  const areaSquared = s * (s - d1) * (s - d2) * (s - d3)

  // Collinear points (straight line) - no turn
  if (areaSquared <= 0.1) return null

  const area = Math.sqrt(areaSquared)
  const radius = (d1 * d2 * d3) / (4 * area)

  // Filter unrealistic radii (too tight or essentially straight)
  if (radius < 5 || radius > 10000) return null

  return radius
}

/**
 * Calculate averaged turn radius using multiple point spans for robustness
 *
 * Takes the median of radii calculated at different offsets to reduce
 * impact of GPS noise on individual measurements.
 */
function calculateAveragedRadius(
  points: GpsPoint[],
  centerIndex: number,
  lookAhead: number
): number | null {
  const validRadii: number[] = []

  for (let offset = 1; offset <= lookAhead; offset++) {
    const prevIndex = centerIndex - offset
    const nextIndex = centerIndex + offset

    if (prevIndex < 0 || nextIndex >= points.length) continue

    const radius = calculateTurnRadiusFromPoints(
      points[prevIndex],
      points[centerIndex],
      points[nextIndex]
    )

    if (radius !== null) validRadii.push(radius)
  }

  if (validRadii.length === 0) return null

  // Use median for robustness against outliers
  validRadii.sort((a, b) => a - b)
  const midIndex = Math.floor(validRadii.length / 2)
  return validRadii.length % 2 === 0
    ? (validRadii[midIndex - 1] + validRadii[midIndex]) / 2
    : validRadii[midIndex]
}

/**
 * Calculate turn direction using heading change
 *
 * @returns +1 for left turn, -1 for right turn
 */
function calculateTurnDirectionSign(
  p1: GpsPoint,
  p2: GpsPoint,
  p3: GpsPoint
): number {
  const heading1 = Math.atan2(
    p2.latitude - p1.latitude,
    p2.longitude - p1.longitude
  )
  const heading2 = Math.atan2(
    p3.latitude - p2.latitude,
    p3.longitude - p2.longitude
  )

  let headingChange = heading2 - heading1
  while (headingChange > Math.PI) headingChange -= 2 * Math.PI
  while (headingChange < -Math.PI) headingChange += 2 * Math.PI

  return headingChange >= 0 ? 1 : -1
}

/**
 * Calculate acceleration from GPS speed changes and turn geometry.
 *
 * Calculates both:
 * - **Longitudinal**: from speed change over time (dv/dt)
 * - **Lateral**: from turn radius using centripetal formula (a = v²/r)
 *
 * Uses same GPS smoothing approach as lean angle calculator for consistent results.
 *
 * @param gpsPoints - GPS points with speed data
 * @param frequencyConfig - Centralized frequency configuration from getFrequencyAdaptiveConfig()
 * @returns Acceleration points aligned with input GPS points
 */
export function calculateAccelerationFromGps(
  gpsPoints: GpsPoint[],
  frequencyConfig: FrequencyAdaptiveConfig
): AccelerationPoint[] {
  const result: AccelerationPoint[] = []

  // Step 1: Smooth GPS coordinates (same as lean angle calculator)
  // Scale window to cover ~1.5 seconds for high-freq data
  const gpsSmoothing = Math.round(15 * frequencyConfig.frequencyScale)
  const smoothedPoints = smoothGpsCoordinates(gpsPoints, gpsSmoothing)

  // Step 2: Calculate lookAhead based on actual frequency (same as lean angle calculator)
  // Need ~3-4 meter separation between points for accurate radius
  // At 10Hz: ~3 points, at 50Hz: ~17 points
  const frequencyHz = frequencyConfig.frequencyScale * 10
  const lookAhead = Math.max(3, Math.ceil(frequencyHz / 3))

  // Derivative stencil: how many points to look ahead/behind for dv/dt.
  // Point-to-point (stencil=1) maximally amplifies GPS speed noise.
  // A wider central difference averages out jitter at the source.
  // At 10Hz: 2 points = 0.2s window, at 25Hz: 4 points = 0.16s, at 50Hz: 8 points = 0.16s
  // Keep small to preserve brake onset precision (~0.2s at 10Hz).
  const derivativeStencil = Math.max(2, Math.round(frequencyHz / 7))

  // Step 3: Calculate raw acceleration for each point
  for (let i = 0; i < gpsPoints.length; i++) {
    const current = gpsPoints[i]

    // Longitudinal acceleration from speed change using central difference.
    // Instead of adjacent-point dv/dt (which amplifies noise), use points
    // ±derivativeStencil apart for a more stable derivative estimate.
    let longitudinalAccel = 0
    const back = Math.max(0, i - derivativeStencil)
    const fwd = Math.min(gpsPoints.length - 1, i + derivativeStencil)
    if (back !== fwd) {
      const deltaSpeed =
        (gpsPoints[fwd].speedKmh - gpsPoints[back].speedKmh) / KMH_PER_MPS
      const deltaTime =
        (gpsPoints[fwd].timestamp.getTime() -
          gpsPoints[back].timestamp.getTime()) /
        MS_PER_S
      longitudinalAccel = deltaTime > 0 ? deltaSpeed / deltaTime : 0
    }

    // Lateral acceleration from turn radius (centripetal: a = v²/r)
    // Use smoothed coordinates for radius calculation (reduces GPS jitter)
    let lateralAccel = 0
    if (
      i >= lookAhead &&
      i < smoothedPoints.length - lookAhead &&
      current.speedKmh >= 10
    ) {
      const radius = calculateAveragedRadius(smoothedPoints, i, lookAhead)

      if (radius !== null) {
        const speedMs = current.speedKmh / KMH_PER_MPS
        const centripetalAccel = (speedMs * speedMs) / radius

        // Limit unrealistic values (>25 m/s² ≈ 2.5g is unrealistic for road vehicles)
        if (centripetalAccel <= 25) {
          // Apply turn direction sign (positive = left, negative = right)
          const sign = calculateTurnDirectionSign(
            smoothedPoints[i - 1],
            smoothedPoints[i],
            smoothedPoints[i + 1]
          )
          lateralAccel = sign * centripetalAccel
        }
      }
    }

    result.push({
      longitudinalMs2: Math.round(longitudinalAccel * 100) / 100,
      lateralMs2: Math.round(lateralAccel * 100) / 100,
      verticalMs2: 0,
      timestamp: current.timestamp,
      distanceMeters: current.distanceMeters,
      elapsedMs: current.elapsedMs,
      source: 'calculated-gps' as const,
    })
  }

  // Step 4: Apply Gaussian smoothing to reduce GPS-derived noise
  // Gaussian weighting preserves peaks better than uniform averaging while
  // eliminating the jagged artifacts from numerical differentiation.
  //
  // Gaussian passes are capped at 3: the central-difference stencil already
  // reduces noise at source, so 5 Gaussian passes at 50Hz (~1.8s blur) would
  // merge separate braking events. Cap keeps 50Hz under ~1.4s effective blur.
  //
  // Base window: 8 points at 10Hz (~0.8s)
  // At 10Hz:  window=8,  passes=1 → ~0.8s effective smoothing
  // At 25Hz:  window=20, passes=2 → ~1.6s effective smoothing
  // At 50Hz:  window=40, passes=3 → ~1.4s effective smoothing (capped from 5)
  // `speedResidualRmsKmh` in analysis/config/gpsDerivedAccel.ts mirrors this
  // exact window/passes formula (applied to speed instead of acceleration)
  // so its residual measures this path's smoothing noise — keep both in sync.
  const smoothingWindow = Math.round(8 * frequencyConfig.frequencyScale)
  const smoothingPasses = Math.min(frequencyConfig.smoothingPasses, 3)

  const longitudinalValues = result.map((p) => p.longitudinalMs2)
  const smoothedLongitudinal = smoothValuesGaussian(
    longitudinalValues,
    smoothingWindow,
    smoothingPasses
  )

  const lateralValues = result.map((p) => p.lateralMs2)
  const smoothedLateral = smoothValuesGaussian(
    lateralValues,
    smoothingWindow,
    smoothingPasses
  )

  // Update acceleration points with smoothed values
  result.forEach((point, i) => {
    point.longitudinalMs2 = Math.round(smoothedLongitudinal[i] * 100) / 100
    point.lateralMs2 = Math.round(smoothedLateral[i] * 100) / 100
  })

  return result
}
